import React from 'react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
          Share Files <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Securely</span>
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          Direct peer-to-peer file sharing in your browser. No accounts, no limits, complete privacy with end-to-end encryption.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/send"
            className="bg-indigo-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-indigo-700 transition shadow-lg hover:shadow-xl"
          >
            Share Files Now
          </Link>
          <Link
            to="/receive"
            className="bg-white text-indigo-600 px-8 py-4 rounded-lg text-lg font-semibold border-2 border-indigo-600 hover:bg-indigo-50 transition"
          >
            Receive Files
          </Link>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        <FeatureCard
          icon="🔒"
          title="End-to-End Encrypted"
          description="Files are encrypted during transfer. Only sender and receiver can access them."
        />
        <FeatureCard
          icon="⚡"
          title="Lightning Fast"
          description="Direct P2P connection using WebRTC for maximum speed."
        />
        <FeatureCard
          icon="📦"
          title="No Size Limits"
          description="Share files of any size - 1MB or 10GB, no restrictions."
        />
        <FeatureCard
          icon="🌐"
          title="Browser Based"
          description="Works directly in your browser. No app installation needed."
        />
      </div>

      {/* How It Works */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
          How It Works
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <StepCard
            number="1"
            title="Select Files"
            description="Choose files you want to share or drag & drop them"
          />
          <StepCard
            number="2"
            title="Share Code"
            description="Get a unique share code and send it to the receiver"
          />
          <StepCard
            number="3"
            title="Transfer"
            description="Files transfer directly between browsers - fast and secure"
          />
        </div>
      </div>

      {/* Security Info */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl shadow-lg p-8 text-white">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">
            Your Privacy, Our Priority
          </h2>
          <p className="text-lg mb-6 opacity-90">
            RayShare uses WebRTC technology to create direct peer-to-peer connections. 
            Your files never touch our servers - they go straight from sender to receiver, 
            encrypted end-to-end.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-white/20 px-4 py-2 rounded-full">✓ No Server Storage</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">✓ Temporary Sessions</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">✓ No Tracking</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">✓ Open Source</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition">
    <div className="text-4xl mb-3">{icon}</div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

const StepCard = ({ number, title, description }) => (
  <div className="text-center">
    <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
      {number}
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

export default HomePage;
